import React from 'react';
import { MotionButton, FadeInSection } from '../ui/MotionPrimitives';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

const ContactDetailsSection: React.FC = () => {
  return (
    <FadeInSection className="w-full bg-white py-16">
      <div className="mx-auto flex max-w-6xl flex-col gap-12 px-6 md:px-10 lg:flex-row lg:items-start">
        {/* Info column */}
        <div className="w-full lg:w-1/2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black">
            Contact Information
          </p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-black md:text-4xl">
            Company Details & Key Contacts
          </h2>

          <div className="mt-6 space-y-4 text-sm leading-relaxed text-black md:text-base">
            <div>
              <p className="font-semibold">Company Name</p>
              <p>Hogar Dorado House</p>
            </div>

            <div>
              <p className="font-semibold">Industry Sector</p>
              <p>Real Estate Investment &amp; Advisory</p>
            </div>

            <div>
              <p className="font-semibold">Headquarters (Global HQ)</p>
              <p>Jumeirah Lake Towers, Dubai, UAE</p>
            </div>

            <div>
              <p className="font-semibold">Official Website</p>
              <p>www.hogardoradohouse.com</p>
            </div>

            <div>
              <p className="font-semibold">General Inquiries</p>
              <p>Email: info@hogardoradohouse.com</p>
            </div>

            <div>
              <p className="font-semibold">CEO Contact (Eric Bobby Brooker)</p>
              <p>Email: eric@hogardoradohouse.com</p>
              <p>UAE Mobile: +971 58 158 4367</p>
              <p>USA Mobile: +1 (912) 704-2632</p>
            </div>

            <div>
              <p className="font-semibold">Relationship Manager (Patience Charingira)</p>
              <p>Email: patience@hogardoradohouse.com</p>
            </div>

            <div className="pt-2">
              <p className="font-semibold mb-2">Connect with us</p>
              <div className="flex items-center gap-3 text-black/70">
                <a
                  href="https://x.com/HogarDoradoHse"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 shadow-sm transition hover:border-[#D88546] hover:text-[#D88546]"
                >
                  <TwitterIcon fontSize="small" />
                </a>
                <a
                  href="https://www.instagram.com/hogardoradohouse/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 shadow-sm transition hover:border-[#D88546] hover:text-[#D88546]"
                >
                  <InstagramIcon fontSize="small" />
                </a>
                <a
                  href="https://www.facebook.com/hogardoradohouse"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 shadow-sm transition hover:border-[#D88546] hover:text-[#D88546]"
                >
                  <FacebookIcon fontSize="small" />
                </a>
                <a
                  href="https://www.linkedin.com/company/hogar-dorado-house/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 shadow-sm transition hover:border-[#D88546] hover:text-[#D88546]"
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon fontSize="small" />
                </a>
                <a
                  href="https://www.tiktok.com/@hogardoradohouse"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-white text-black/70 shadow-sm transition hover:border-[#D88546] hover:text-[#D88546]"
                  aria-label="TikTok"
                >
                  <MusicNoteIcon fontSize="small" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm lg:w-1/2 md:p-8">
          <h3 className="text-xl font-semibold tracking-tight text-black md:text-2xl">
            Send us a message
          </h3>
          <p className="mt-2 text-sm text-black md:text-base">
            Share a bit about your requirements and our team will respond with tailored guidance.
          </p>

          <form
            className="mt-6 space-y-4"
            method="POST"
            action="https://formsubmit.co/info@hogardoradohouse.com"
          >
            {/* FormSubmit helpers */}
            <input type="hidden" name="_subject" value="New contact from Hogar Dorado House website" />
            <input type="hidden" name="_captcha" value="false" />
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="w-full">
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-black">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
                />
              </div>
              <div className="w-full">
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-black">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-black">
                Subject
              </label>
              <input
                type="text"
                name="subject"
                className="mt-2 w-full rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-black">
                Message
              </label>
              <textarea
                rows={6}
                name="message"
                className="mt-2 w-full rounded-2xl border border-gray-300 bg-white px-4 py-2 text-sm text-black outline-none transition focus:border-[#D88546] focus:ring-2 focus:ring-[#D88546]/40"
              />
            </div>

            <MotionButton
              type="submit"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-[#D88546] px-8 py-2.5 text-sm font-semibold uppercase tracking-wide text-black shadow-md transition hover:bg-[#c1733d] focus:outline-none focus:ring-2 focus:ring-[#D88546] focus:ring-offset-2 focus:ring-offset-white"
            >
              Send Message
            </MotionButton>
          </form>
        </div>
      </div>
    </FadeInSection>
  );
};

export default ContactDetailsSection;
